import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, SearchIcon, Calendar, Filter } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

// Number of recent articles linked per category card in the default view.
const RECENT_PER_CATEGORY = 6;

const ArticlesPage = () => {
  // The article library is a build-time snapshot (src/data/articlesIndex.json)
  // emitted as its own webpack chunk. Loading it via dynamic import keeps the
  // ~350KB index out of the main bundle while still resolving same-origin
  // during the react-snap prerender, so the category cards and article links
  // below are baked into the static HTML that crawlers read.
  const [index, setIndex] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    import('../data/articlesIndex.json')
      .then((mod) => {
        if (!cancelled) setIndex(mod.default || mod);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const articles = index ? index.articles : [];
  const categories = index ? index.categories : [];

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const isFiltering = normalizedSearch !== '' || selectedCategory !== null;

  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      normalizedSearch === '' ||
      article.title.toLowerCase().includes(normalizedSearch) ||
      (article.excerpt || '').toLowerCase().includes(normalizedSearch);
    const matchesCategory =
      selectedCategory === null || article.category_slug === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Featured interactive article (a React page, not a snapshot article).
  const featured = {
    title: "Sortio's AI Just Got 5x Faster: Here's the Data",
    excerpt:
      'We benchmarked 7 AI models across thousands of files. See the results: per-model sort speed, completion rates, and cost per 100 files.',
    created_at: '2026-03-31T00:00:00Z',
    category_name: 'Digital Workspace Productivity',
    url: '/articles/ai-model-upgrade',
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Background blur orbs */}
      <div className="fixed -bottom-32 -right-32 w-96 h-96 bg-sortio-primary/10 rounded-full blur-3xl"></div>
      <div className="fixed -top-32 -left-32 w-96 h-96 bg-sortio-secondary/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        <Helmet>
          <title>Articles - Sortio</title>
          <meta name="description" content="Explore insightful articles on AI-powered file organization and more." />
          <link rel="canonical" href="https://www.getsortio.com/articles" />
          <meta property="og:title" content="Articles - Sortio" />
          <meta property="og:description" content="Explore insightful articles on AI-powered file organization and more." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.getsortio.com/articles" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Articles - Sortio" />
          <meta name="twitter:description" content="Explore insightful articles on AI-powered file organization and more." />
        </Helmet>

        <Breadcrumbs category="Articles" articleTitle="" />

        {/* Page title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-apple-50 to-sortio-primary">Articles & Resources</span>
          </h1>
          <p className="text-apple-300 max-w-2xl mx-auto">
            {index
              ? `${articles.length} articles on file organization, digital productivity, and AI-powered sorting, organized into ${categories.length} categories.`
              : 'Insights, tips, and guides on AI-powered file organization and digital productivity.'}
          </p>
        </div>

        {/* Search */}
        <div className="apple-panel p-6 mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-apple-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-apple-700 rounded-xl bg-apple-800/50 text-apple-100 placeholder-apple-400 focus:outline-none focus:ring-2 focus:ring-sortio-primary focus:border-transparent transition-all"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Categories pills */}
        {categories.length > 0 && (
          <div className="mb-12">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full transition-all ${selectedCategory === null
                  ? 'bg-sortio-primary text-black font-medium'
                  : 'bg-apple-800/50 text-apple-300 hover:bg-apple-700/70'}`}
              >
                All
              </button>

              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`px-4 py-2 rounded-full transition-all ${selectedCategory === category.slug
                    ? 'bg-sortio-primary text-black font-medium'
                    : 'bg-apple-800/50 text-apple-300 hover:bg-apple-700/70'}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!index && !loadFailed && (
          <div className="flex justify-center">
            <div className="apple-panel p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sortio-primary mx-auto mb-4"></div>
              <p className="text-apple-300">Loading articles...</p>
            </div>
          </div>
        )}

        {loadFailed && (
          <div className="apple-panel p-8 text-center border-red-500/20">
            <p className="text-red-400">Unable to load the article library. Please try again later.</p>
          </div>
        )}

        {index && !isFiltering && (
          <>
            {/* Featured interactive article */}
            <article className="apple-panel group p-6 relative overflow-hidden mb-8 border-sortio-primary/30">
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-sortio-primary/10 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>

              <span className="inline-block px-3 py-1 text-xs font-semibold bg-sortio-primary/20 text-sortio-primary rounded-full mb-3">
                Featured
              </span>

              <h2 className="text-3xl font-semibold mb-3 group-hover:text-sortio-primary transition-colors">
                <Link to={featured.url}>{featured.title}</Link>
              </h2>

              <div className="flex items-center space-x-4 mb-4 text-sm text-apple-400">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(featured.created_at)}
                </span>
                <span className="flex items-center">
                  <Filter className="w-4 h-4 mr-1" />
                  {featured.category_name}
                </span>
              </div>

              <p className="text-apple-300 mb-5">{featured.excerpt}</p>

              <Link
                to={featured.url}
                className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80 transition-colors group-hover:underline"
              >
                Read more
                <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </article>

            {/* Category overview: every category links to its full archive,
                and the latest articles in each are linked directly so the
                library is browsable without JavaScript. */}
            <div className="grid md:grid-cols-2 gap-8">
              {categories.map((category) => {
                const categoryArticles = articles.filter(
                  (a) => a.category_slug === category.slug
                );
                const recent = categoryArticles.slice(0, RECENT_PER_CATEGORY);
                return (
                  <section key={category.slug} className="apple-panel p-6">
                    <h2 className="text-2xl font-semibold mb-1">
                      <Link
                        to={`/articles/${category.slug}`}
                        className="hover:text-sortio-primary transition-colors"
                      >
                        {category.name}
                      </Link>
                    </h2>
                    <p className="text-sm text-apple-400 mb-4">
                      {category.count} {category.count === 1 ? 'article' : 'articles'}
                    </p>
                    <ul className="space-y-3 mb-5">
                      {recent.map((article) => (
                        <li key={article.slug}>
                          <Link
                            to={`/articles/${article.category_slug}/${article.slug}`}
                            className="text-apple-200 hover:text-sortio-primary transition-colors"
                          >
                            {article.title}
                          </Link>
                          <span className="block text-xs text-apple-400 mt-0.5">
                            {formatDate(article.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/articles/${category.slug}`}
                      className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80 transition-colors"
                    >
                      View all {category.count} {category.count === 1 ? 'article' : 'articles'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </section>
                );
              })}
            </div>
          </>
        )}

        {index && isFiltering && (
          <>
            <p className="text-apple-400 mb-6">
              {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'} found
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {filteredArticles.map((article) => {
                const articleUrl = `/articles/${article.category_slug}/${article.slug}`;
                return (
                  <article key={`${article.category_slug}/${article.slug}`} className="apple-panel group p-6 relative overflow-hidden">
                    <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-sortio-primary/10 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>

                    <h2 className="text-2xl font-semibold mb-3 group-hover:text-sortio-primary transition-colors">
                      <Link to={articleUrl}>{article.title}</Link>
                    </h2>

                    <div className="flex items-center space-x-4 mb-4 text-sm text-apple-400">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(article.created_at)}
                      </span>
                      <span className="flex items-center">
                        <Filter className="w-4 h-4 mr-1" />
                        {article.category}
                      </span>
                    </div>

                    <p className="text-apple-300 mb-5">{article.excerpt}</p>

                    <Link
                      to={articleUrl}
                      className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80 transition-colors group-hover:underline"
                    >
                      Read more
                      <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </article>
                );
              })}
            </div>

            {filteredArticles.length === 0 && (
              <div className="apple-panel p-8 text-center">
                <p className="text-apple-300">No articles found matching your criteria.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ArticlesPage;
